<template>
  <div>
  <AIVDialog
    v-model="isVisible"
    title="Clock In"
    width="40%"
    height="85vh"
    class="mobile-friendly-modal clock-in-modal"
  >
    <div class="clock-modal-content">
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p>
          <strong>Date:</strong> {{ formatTableDate(shift?.date) }}
        </p>
        <p>
          <strong>Time:</strong>
          {{
            formatTimeRange(
              shift?.scheduled_start || shift?.startTime,
              shift?.scheduled_end || shift?.endTime,
              shift?.date
            )
          }}
        </p>
        <p>
          <strong>Payer:</strong>
          {{ shift?.payer || shift?.originalData?.payer || "Not specified" }}
        </p>
        <p v-if="shift?.payer !== 'Non Service'"><strong>Client:</strong> {{ formatClient(shift) }}</p>
        <p><strong>Caregiver:</strong> {{ formatCaregiver(shift) }}</p>
        <p v-if="clientLocationInfo && shift?.payer !== 'Non Service' && shift?.shift_type !== 'Group'">
          <strong>Client Address:</strong> {{ clientLocationInfo.address }}
          <el-button
            type="text"
            size="small"
            @click="openMapLinkFromAddress(clientLocationInfo.address)"
            class="map-link-btn"
          >
            <i class="fa-solid fa-location-dot"></i> View on Map
          </el-button>
        </p>

        <!-- Admin Notes -->
        <div v-if="shiftNotes" class="shift-notes-section">
          <strong><i class="fa-solid fa-sticky-note"></i> Notes:</strong>
          <div class="shift-notes-content">{{ shiftNotes }}</div>
        </div>

        <!-- Service Code -->
        <p v-if="availableServiceCodes.length === 1">
          <strong>Service Code:</strong> {{ availableServiceCodes[0].code }}
        </p>
        <div v-else-if="availableServiceCodes.length > 1" class="service-code-row">
          <label class="form-label service-code-label">Service Code:</label>
          <AIVSelect
            v-model="selectedServiceCodeId"
            :options="availableServiceCodes"
            placeholder="Select"
            :custom-style="{
              backgroundColor: '#656565',
              minWidth: '120px',
              maxWidth: '150px',
            }"
          />
        </div>
        <div v-else class="no-service-code-message">
          <div class="alert alert-danger">
            <i class="fa-solid fa-exclamation-triangle"></i>
            No service codes available for this shift. Cannot clock in.
          </div>
        </div>

        <!-- Virtual meeting link (for virtual shifts) -->
        <div v-if="isVirtualShift && shift && shift.virtual_link" class="location-section">
          <label class="form-label">Meeting Link:</label>
          <div class="location-info-card">
            <div class="location-row">
              <span class="location-label">
                <i class="fa-solid fa-video"></i> Meeting:
              </span>
              <a
                :href="shift.virtual_link"
                target="_blank"
                rel="noopener noreferrer"
                class="location-value virtual-link-value"
              >
                {{ shift.virtual_link }}
              </a>
            </div>
          </div>
        </div>

        <!-- Shift location display for DSPD clients (not for group shifts - locations shown in tasks modal) -->
        <!-- Location is determined at shift creation, not at clock-in -->

        <!-- Geofence-bypass banner (per-caregiver or per-client opt-out) -->
        <div v-if="geofenceBypassed && !isVirtualShift" class="geofence-bypass-banner mb-2">
          <i class="fa-solid fa-circle-info"></i>
          {{ geofenceBypassReason }}
        </div>

        <!-- Capture-only note: location is recorded but not enforced -->
        <div v-if="!requiresLocation && !locationCaptureDisabled && !isVirtualShift" class="geofence-bypass-banner mb-2">
          <i class="fa-solid fa-location-dot"></i>
          Your clock-in location will be recorded.
        </div>

        <!-- Location capture and distance (only for LS and JC service codes) -->
        <div v-if="requiresLocation && !isVirtualShift" class="location-section">
          <label class="form-label">Location Information:</label>

          <div class="location-info-card">
            <!-- Your Location -->
            <div class="location-row">
              <span class="location-label"><i class="fa-solid fa-user"></i> Your Location:</span>
              <span v-if="currentLocation" class="location-value">
                {{ currentLocation.latitude.toFixed(6) }}, {{ currentLocation.longitude.toFixed(6) }}
              </span>
              <span v-else-if="gettingLocation" class="location-pending">
                <i class="fa-solid fa-spinner fa-spin"></i> Getting your location...
              </span>
              <span v-else class="location-pending">
                Location unavailable
              </span>
            </div>

            <!-- Client Location (nearest of multiple, when applicable) -->
            <div v-if="clientLocationInfo" class="location-row">
              <span class="location-label"><i class="fa-solid fa-building"></i> Client Location:</span>
              <span class="location-value">
                {{ clientLocationInfo.address }}
                <span v-if="extraAddressCount > 0" class="extra-address-hint">
                  (nearest of {{ extraAddressCount + 1 }} addresses checked)
                </span>
              </span>
            </div>

            <!-- Distance -->
            <div v-if="currentLocation && clientLocationInfo?.geocode" class="location-row">
              <span class="location-label"><i class="fa-solid fa-route"></i> Distance:</span>
              <span class="location-value">
                {{ formatDistance(calculatedDistance) }}
              </span>
            </div>

            <!-- Accuracy -->
            <div v-if="currentLocation" class="location-row">
              <span class="location-label"><i class="fa-solid fa-crosshairs"></i> Accuracy:</span>
              <span class="location-value" :class="getAccuracyClass(currentLocation.accuracy)">
                &#177;{{ Math.round(currentLocation.accuracy) }}m
                <template v-if="currentLocation.accuracy < 50">(Good)</template>
                <template v-else-if="currentLocation.accuracy < 150">(Moderate)</template>
                <template v-else>(Low)</template>
              </span>
            </div>

            <!-- Low accuracy warning -->
            <div v-if="currentLocation && isAccuracyTooLow" class="accuracy-warning-banner">
              <i class="fa-solid fa-exclamation-triangle"></i>
              Location accuracy is too low (&#177;{{ Math.round(currentLocation.accuracy) }}m) for reliable geofencing.
              Move to an open area or wait for a better GPS signal.
            </div>

            <!-- Manual refresh + inline location error -->
            <div class="location-row location-refresh-row">
              <el-button
                size="mini"
                icon="el-icon-refresh"
                :loading="gettingLocation"
                @click="requestLocation"
              >
                Refresh location
              </el-button>
              <span v-if="locationError" class="location-error">{{ locationError }}</span>
            </div>

          </div>

          <!-- Geofence override section -->
          <div v-if="showGeofenceOverride" class="geofence-override-section">
            <div class="geofence-override-banner">
              <i class="fa-solid fa-exclamation-triangle"></i>
              <div>
                <strong v-if="!currentLocation">
                  We couldn't capture your location. Tap "Refresh location" above to try again,
                  or override the location check below to clock in without it.
                </strong>
                <strong v-else-if="isAccuracyTooLow && !isWithinAllowedDistance">
                  Location accuracy is low and you appear to be {{ formatDistance(calculatedDistance) }} from the client.
                </strong>
                <strong v-else-if="isAccuracyTooLow">
                  Location accuracy is too low (&#177;{{ Math.round(currentLocation.accuracy) }}m) for reliable geofencing.
                </strong>
                <strong v-else>
                  You appear to be {{ formatDistance(calculatedDistance) }} from the client location.
                  You must be within {{ formatDistance(maxAllowedDistance) }} to clock in.
                </strong>
                <p class="override-hint">
                  If you believe you are at the correct location and this is a GPS signal issue,
                  you may override the location check below. Your location will be recorded.
                </p>
              </div>
            </div>
            <el-checkbox v-model="geofenceOverrideAcknowledged" class="geofence-override-checkbox">
              I am at the correct location. I understand my location is being logged.
            </el-checkbox>
          </div>
        </div>
      </div>

      <!-- Client Medications -->
      <div v-if="clientMedicationGroups.length > 0 && shift?.payer === 'DSPD' && shift?.shift_type !== 'Group'" class="mb-3">
        <h5>Client Medications</h5>
        <div v-for="group in clientMedicationGroups" :key="group.clientId">
          <div v-if="clientMedicationGroups.length > 1" class="medication-client-name">
            {{ group.clientName }}
          </div>
          <div v-for="med in group.medications" :key="med.id" class="medication-info-item">
            <div class="medication-name">{{ med.name }}</div>
            <div class="medication-details">
              <span v-if="med.dosage">{{ med.dosage }}</span>
              <span v-if="med.frequency"> - {{ med.frequency }}</span>
              <span v-if="med.administration_times && med.administration_times.length > 0"> ({{ med.administration_times.join(', ') }})</span>
            </div>
            <div v-if="med.instructions" class="medication-instructions">
              <small>{{ med.instructions }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Compliance Warning -->
      <div v-if="complianceCheck && complianceCheck.blocks && complianceCheck.blocks.length > 0" class="mb-3">
        <div class="alert alert-danger" style="background: rgba(244, 67, 54, 0.15); border: 1px solid rgba(244, 67, 54, 0.4); border-radius: 6px; padding: 12px 15px; color: #f44336;">
          <i class="fa-solid fa-ban mr-1"></i>
          <strong>Compliance block:</strong> {{ complianceCheck.blocks[0].message }}
        </div>
      </div>
      <div v-else-if="complianceCheck && complianceCheck.warnings && complianceCheck.warnings.length > 0" class="mb-3">
        <div class="alert alert-warning" style="background: rgba(255, 152, 0, 0.15); border: 1px solid rgba(255, 152, 0, 0.4); border-radius: 6px; padding: 12px 15px; color: #FF9800;">
          <i class="fa-solid fa-exclamation-triangle mr-1"></i>
          <strong>Compliance warning:</strong> {{ complianceCheck.warnings[0].message }}
          <span v-if="complianceCheck.warnings.length > 1"> (+{{ complianceCheck.warnings.length - 1 }} more)</span>
        </div>
      </div>

      <!-- Support Strategy Warning -->
      <div v-if="supportStrategyCheck && supportStrategyCheck.required && !supportStrategyCheck.acknowledged" class="mb-3">
        <div class="alert alert-warning" style="background: rgba(255, 193, 7, 0.15); border: 1px solid rgba(255, 193, 7, 0.4); border-radius: 6px; padding: 12px 15px; color: #ffc107;">
          <i class="fa-solid fa-exclamation-triangle mr-1"></i>
          <strong>Support Strategy acknowledgment required.</strong>
          You must read and acknowledge the Support Strategy before clocking in.
        </div>
      </div>

      <!-- Mileage Tracking (Optional at clock-in; if a starting odometer is
           entered, the ending odometer/locations become required at clock-out) -->
      <div class="mileage-section mb-3">
        <h6><i class="fa-solid fa-car mr-1"></i> Mileage Tracking</h6>
        <div class="mileage-fields">
          <div class="row">
            <div class="col-md-6 mb-2">
              <label class="form-label">Starting Odometer</label>
              <el-input
                v-model="odometerStart"
                type="number"
                placeholder="Starting odometer reading"
                size="small"
              />
            </div>
            <div class="col-md-6 mb-2">
              <label class="form-label">Starting Location</label>
              <el-input
                v-model="mileageStartLocation"
                placeholder="e.g. Client's home, Office"
                size="small"
              />
            </div>
          </div>
        </div>
        <el-checkbox v-model="isTransporting" class="mt-1">
          Transporting client(s) during this shift
        </el-checkbox>
        <div v-if="odometerStart" class="mileage-hint">
          <small class="text-muted">
            You'll be asked for your ending odometer, places visited, and
            ending location at clock-out.
          </small>
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button
          type="primary"
          class="clock-in-btn"
          @click="isVirtualShift ? confirmClockInAndJoinMeeting() : confirmClockIn()"
          :loading="clockingIn"
        >
          <i :class="needsSupportStrategyAck ? 'fa-solid fa-file-alt' : isVirtualShift ? 'fa-solid fa-video' : 'fa-solid fa-clock'"></i>
          {{ needsSupportStrategyAck ? 'View Support Strategy' : isVirtualShift ? 'Clock In & Join Meeting' : 'Clock In' }}
        </el-button>
      </div>
    </template>
  </AIVDialog>

  <SupportStrategyAcknowledgeModal
    v-model="showSupportStrategyModal"
    :document="supportStrategyCheck ? supportStrategyCheck.document : null"
    :clientName="formatClient(shift)"
    @acknowledged="onSupportStrategyAcknowledged"
  />

  <ComplianceWarningModal
    :visible.sync="showComplianceWarningModal"
    :warnings="complianceCheck ? (complianceCheck.warnings || []) : []"
    :blocks="complianceCheck ? (complianceCheck.blocks || []) : []"
    @proceed="onComplianceWarningProceed"
  />
  </div>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { geolocationService } from "@/serviceHandlers/geolocationService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { serviceCodesService } from "@/serviceHandlers/serviceCodesService";
import { nonServiceCodesService } from "@/serviceHandlers/nonServiceCodesService";
import { documentAcknowledgmentsService } from "@/serviceHandlers/documentAcknowledgmentsService";
import { caregiverComplianceService } from "@/serviceHandlers/caregiverComplianceService";
import SupportStrategyAcknowledgeModal from "@/components/SupportStrategyAcknowledgeModal.vue";
import ComplianceWarningModal from "@/components/ComplianceWarningModal.vue";
import { enqueue, generateEventId } from "@/utils/offlineQueue";
import { prefetchShiftClockData } from "@/utils/clockPrefetch";

export default {
  name: "ClockInModal",
  components: { AIVDialog, AIVSelect, SupportStrategyAcknowledgeModal, ComplianceWarningModal },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    shift: {
      type: Object,
      default: () => ({})
    },
    userId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      clockingIn: false,
      gettingLocation: false,
      currentLocation: null,
      currentLocationAddress: null,
      selectedServiceCodeId: null,
      selectedLocationIndex: 0,
      isVRDistanceFlagged: false,
      locationWatchId: null,
      locationError: null,
      calculatedDistance: null,
      maxAllowedDistance: 0.25, // 0.25 miles (1320 feet)
      availableServiceCodes: [],
      clientLocations: [],
      loadingServiceCodes: false,
      clientMedicationGroups: [],
      supportStrategyCheck: null,
      showSupportStrategyModal: false,
      checkingRequirements: false,
      complianceCheck: null,
      showComplianceWarningModal: false,
      isTransporting: false,
      mileageStartLocation: '',
      odometerStart: null,
      geofenceOverrideAcknowledged: false,
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    },

    shiftNotes() {
      if (!this.shift) return "";
      return this.shift.shift_notes || this.shift.notes || "";
    },

    isVirtualShift() {
      return !!(this.shift?.virtual_link);
    },

    needsSupportStrategyAck() {
      return !!(this.supportStrategyCheck?.required && !this.supportStrategyCheck?.acknowledged);
    },

    requiresLocation() {
      if (this.geofenceBypassed) return false;
      if (!this.selectedServiceCodeId) return false;
      const selectedCode = this.availableServiceCodes.find(sc => sc.id === this.selectedServiceCodeId);
      if (!selectedCode) return false;
      // Admin-configurable per service code (Settings → Service Codes).
      if (selectedCode.geofence_mode) {
        return selectedCode.geofence_mode === 'enforce';
      }
      // Fallback for payloads predating the geofence_mode column.
      const geofencedCodes = ['LS', 'SLN', 'SLH', 'CMP'];
      return geofencedCodes.includes(selectedCode.code);
    },

    // 'off' codes store no location at all: no tracking, no geocode sent.
    locationCaptureDisabled() {
      if (!this.selectedServiceCodeId) return false;
      const selectedCode = this.availableServiceCodes.find(sc => sc.id === this.selectedServiceCodeId);
      return selectedCode?.geofence_mode === 'off';
    },

    geofenceBypassed() {
      if (!this.shift) return false;
      const caregiver = this.shift.caregiver
        || (this.shift.caregivers && this.shift.caregivers[0])
        || null;
      if (caregiver && caregiver.bypass_geofence) return true;
      const shiftClients = this.shift.clients || this.shift.originalData?.clients || [];
      return shiftClients.some(c => c && c.bypass_geofence);
    },

    geofenceBypassReason() {
      if (!this.geofenceBypassed) return null;
      const caregiver = this.shift?.caregiver
        || (this.shift?.caregivers && this.shift.caregivers[0])
        || null;
      if (caregiver && caregiver.bypass_geofence) {
        const name = `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.trim() || 'this caregiver';
        return `Location check bypassed for ${name}.`;
      }
      const shiftClients = this.shift?.clients || this.shift?.originalData?.clients || [];
      const bypassedClient = shiftClients.find(c => c && c.bypass_geofence);
      if (bypassedClient) {
        const name = `${bypassedClient.first_name || ''} ${bypassedClient.last_name || ''}`.trim() || 'this client';
        return `Location check bypassed for ${name}.`;
      }
      return 'Location check bypassed.';
    },

    isDSPDClient() {
      const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
      return this.shift?.payer === 'DSPD' || (
        clients.length > 0 &&
        clients.some(client => client.payer === 'DSPD')
      );
    },

    candidateLocations() {
      if (!this.shift) return [];

      const list = [];
      const pushLocation = (loc, source) => {
        if (!loc) return;
        const parts = [];
        if (loc.address1) parts.push(loc.address1);
        if (loc.address2) parts.push(loc.address2);
        if (loc.city) parts.push(loc.city);
        if (loc.state) parts.push(loc.state);
        if (loc.zip) parts.push(loc.zip);
        const address = parts.join(', ');
        if (!address) return;
        const geocode = loc.geocode
          ? geolocationService.parseGeocodeString(loc.geocode)
          : null;
        list.push({ address, geocode, source });
      };

      // Priority 1: shift's separate clock-in address (per-shift override)
      if (this.shift.use_separate_clock_addresses && this.shift.clockInAddress?.address1) {
        pushLocation(this.shift.clockInAddress, 'shift_clock_in_address');
      }
      // Priority 2: VR alternate address
      if (this.shift.alternateAddress?.address1) {
        pushLocation(this.shift.alternateAddress, 'shift_alternate_address');
      }

      // Priority 3: every client address. clientLocations is populated by
      // fetchShiftData; fall back to inline shift client data if needed.
      if (this.clientLocations.length > 0) {
        this.clientLocations.forEach((loc) => pushLocation(loc, 'client_address'));
      } else {
        const clients = this.shift.clients || this.shift.originalData?.clients || [];
        clients.forEach((client) => {
          if (client.addresses && Array.isArray(client.addresses)) {
            client.addresses.forEach((addr) => pushLocation({
              address1: addr.address1 || addr.street || addr.address,
              address2: addr.address2,
              city: addr.city,
              state: addr.state,
              zip: addr.zip || addr.postal_code,
              geocode: addr.geocode,
            }, 'client_address'));
          } else if (client.address && typeof client.address === 'object') {
            pushLocation({
              address1: client.address.address1 || client.address.street || client.address.address,
              address2: client.address.address2,
              city: client.address.city,
              state: client.address.state,
              zip: client.address.zip || client.address.postal_code,
              geocode: client.address.geocode,
            }, 'client_address');
          } else if (typeof client.address === 'string') {
            pushLocation({
              address1: client.address,
              address2: '',
              city: '',
              state: '',
              zip: '',
              geocode: client.geocode,
            }, 'client_address');
          }
        });
      }

      return list;
    },

    candidateDistances() {
      if (!this.currentLocation) return [];
      return this.candidateLocations.map((loc) => {
        if (!loc.geocode) return { ...loc, distance: null };
        const distance = geolocationService.calculateDistance(
          this.currentLocation.latitude,
          this.currentLocation.longitude,
          loc.geocode.latitude,
          loc.geocode.longitude,
        );
        return { ...loc, distance };
      });
    },

    nearestCandidate() {
      const withDistance = this.candidateDistances.filter(c => c.distance != null);
      if (withDistance.length === 0) return null;
      return withDistance.reduce((best, c) => (c.distance < best.distance ? c : best));
    },

    clientLocationInfo() {
      // Maintained for backward compatibility with the rest of the template/methods.
      // When we have a measured nearest candidate, return that — that's the one
      // we want the user to see + the one we'll use for distance display.
      if (this.nearestCandidate) {
        return {
          address: this.nearestCandidate.address,
          geocode: this.nearestCandidate.geocode,
        };
      }
      // No location yet (or no geocoded addresses) — fall back to the first
      // candidate so the address line still renders.
      const first = this.candidateLocations[0];
      return first ? { address: first.address, geocode: first.geocode } : null;
    },

    extraAddressCount() {
      return Math.max(0, this.candidateLocations.length - 1);
    },

    isWithinAllowedDistance() {
      // Pass if ANY candidate address is within range.
      const distances = this.candidateDistances
        .map(c => c.distance)
        .filter(d => d != null);
      if (distances.length === 0) return true;
      return distances.some(d => d <= this.maxAllowedDistance);
    },

    isAccuracyTooLow() {
      if (!this.currentLocation || !this.currentLocation.accuracy) return false;
      // Accuracy worse than half the geofence radius (in meters) is unreliable
      const maxAccuracyMeters = (this.maxAllowedDistance * 1609.34) / 2; // ~200m for 0.25mi radius
      return this.currentLocation.accuracy > maxAccuracyMeters;
    },

    showGeofenceOverride() {
      if (!this.requiresLocation || this.isVirtualShift) return false;
      // No fix captured yet (common while driving — GPS is slow/flaky in
      // motion). Offer the override path instead of dead-ending the caregiver.
      if (!this.currentLocation) return true;
      return this.isAccuracyTooLow || !this.isWithinAllowedDistance;
    },

  },

  mounted() {
    this.checkForAutoSelection();
  },

  beforeDestroy() {
    this.clearLocationUpdateTimers();
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
        this.$nextTick(async () => {
          await this.fetchShiftData();
          this.checkForAutoSelection();
          // Capture-only location: always record where the clock-in happens,
          // even for service codes with no geofence enforcement.
          if (!this.isVirtualShift && this.locationWatchId === null) {
            this.startLocationTracking();
          }
          this.fetchClientMedications();
        });
      } else {
        this.clearLocationUpdateTimers();
      }
    },

    shift: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.$nextTick(async () => {
            await this.fetchShiftData();
            this.checkForAutoSelection();
          });
        }
      },
      deep: true
    },

    selectedServiceCodeId() {
      // Location is always captured (never stopped when the code changes);
      // whether it is *enforced* is decided by requiresLocation at submit.
      if (!this.isVirtualShift && this.locationWatchId === null) {
        this.startLocationTracking();
      }
    },

    selectedLocationIndex() {
      // Recalculate distance when location selection changes
      this.calculateDistance();
    },

    currentLocation: {
      handler() {
        this.calculateDistance();
      },
      deep: true
    },

    availableServiceCodes: {
      handler() {
        this.$nextTick(() => {
          this.checkForAutoSelection();
        });
      },
      immediate: true
    },

    isTransporting(newVal) {
      if (newVal && !this.mileageStartLocation && this.clientLocationInfo) {
        this.mileageStartLocation = this.clientLocationInfo.address;
      }
    },

    odometerStart(newVal) {
      if (newVal && !this.mileageStartLocation && this.clientLocationInfo) {
        this.mileageStartLocation = this.clientLocationInfo.address;
      }
    }
  },

  methods: {
    openMapLinkFromAddress(address) {
      const encodedAddress = encodeURIComponent(address);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, '_blank');
    },
    resetForm() {
      this.currentLocation = null;
      this.currentLocationAddress = null;
      this.selectedServiceCodeId = null;
      this.selectedLocationIndex = 0;
      this.isVRDistanceFlagged = false;
      this.calculatedDistance = null;
      this.availableServiceCodes = [];
      this.clientLocations = [];
      this.clientMedicationGroups = [];
      this.supportStrategyCheck = null;
      this.showSupportStrategyModal = false;
      this.checkingRequirements = false;
      this.complianceCheck = null;
      this.showComplianceWarningModal = false;
      this.isTransporting = false;
      this.mileageStartLocation = '';
      this.odometerStart = null;
      this.geofenceOverrideAcknowledged = false;
      this.clearLocationUpdateTimers();
    },

    async fetchClientMedications() {
      if (!this.shift || this.shift.payer !== 'DSPD') return;
      const shiftClients = this.shift.clients || this.shift?.originalData?.clients || [];
      if (shiftClients.length === 0) return;
      const groups = [];
      for (const client of shiftClients) {
        if (!client.id) continue;
        const clientName = client.name ||
          `${client.first_name || ''} ${client.last_name || ''}`.trim() ||
          `Client ${client.id}`;
        try {
          const response = await clientsService.getMedications(client.id);
          const meds = (response.medications || []).filter(m => m.active !== false);
          if (meds.length > 0) {
            groups.push({ clientId: client.id, clientName, medications: meds });
          }
        } catch (e) { /* skip client on error */ }
      }
      this.clientMedicationGroups = groups;
    },

    async fetchShiftData() {
      if (!this.shift) {
        return;
      }

      // Determine if this is a non-service shift
      const isNonServiceShift = this.shift?.payer === 'Non Service';

      const clients = this.shift?.clients || this.shift?.originalData?.clients || [];

      // Only check for clients if this is not a non-service shift
      if (!isNonServiceShift && (!clients || clients.length === 0)) {
        return;
      }

      const client = clients.length > 0 ? clients[0] : null;

      // Get service codes for this specific shift
      // Check if shift has service_codes/non_service_codes (from API) or selectedServiceCodes (from shift modal)
      const shiftServiceCodes = isNonServiceShift
        ? (this.shift?.non_service_codes || this.shift?.nonServiceCodes || this.shift?.originalData?.non_service_codes || [])
        : (this.shift?.service_codes || this.shift?.serviceCodes || []);
      const selectedServiceCodeIds = this.shift?.selectedServiceCodes || [];

      if (shiftServiceCodes.length > 0) {
        // Use the service codes that are associated with this shift from the database
        this.availableServiceCodes = shiftServiceCodes.map(sc => ({
          id: sc.id,
          name: sc.code,
          code: sc.code,
          geofence_mode: sc.geofence_mode
        }));
      } else if (selectedServiceCodeIds.length > 0) {
        // Use the service codes that were selected when the shift was created
        this.loadingServiceCodes = true;
        try {
          // Load from appropriate service based on shift payer
          const allServiceCodes = isNonServiceShift
            ? await nonServiceCodesService.list()
            : await serviceCodesService.listVR(); // Only load VR service codes for shifts
          this.availableServiceCodes = allServiceCodes
            .filter(sc => selectedServiceCodeIds.includes(sc.id))
            .map(sc => ({
              id: sc.id,
              name: sc.code,
              code: sc.code,
              geofence_mode: sc.geofence_mode
            }));
        } catch (error) {
          this.availableServiceCodes = [];
        } finally {
          this.loadingServiceCodes = false;
        }
      }

      this.clientLocations = [];

      // Skip client locations for group shifts - locations are shown in the tasks modal
      if (this.shift?.shift_type === 'Group') {
        return;
      }

      // Only load client locations if client exists (not for Non Service shifts)
      if (client) {
        if (client.addresses && Array.isArray(client.addresses) && client.addresses.length > 0) {
          this.clientLocations = client.addresses.map((addr) => ({
            address1: addr.address1 || addr.street || addr.address,
            address2: addr.address2,
            city: addr.city,
            state: addr.state,
            zip: addr.zip || addr.postal_code,
            geocode: addr.geocode
          }));
        } else if (client.address && typeof client.address === 'object') {
          this.clientLocations.push({
            address1: client.address.address1 || client.address.street || client.address.address,
            address2: client.address.address2,
            city: client.address.city,
            state: client.address.state,
            zip: client.address.zip || client.address.postal_code,
            geocode: client.address.geocode
          });
        } else if (typeof client.address === 'string') {
          this.clientLocations.push({
            address1: client.address,
            address2: '',
            city: '',
            state: '',
            zip: '',
            geocode: client.geocode
          });
        }
      }

      // Check Support Strategy acknowledgment requirements
      if (!isNonServiceShift && client && this.userId) {
        try {
          this.supportStrategyCheck = await documentAcknowledgmentsService.checkClockInRequirements(
            this.userId,
            client.id
          );
        } catch (e) {
          // Non-blocking: if the check fails, don't prevent clock-in
          this.supportStrategyCheck = null;
        }
      }

      // Check compliance document requirements
      if (this.userId) {
        try {
          const clientId = client ? client.id : null;
          this.complianceCheck = await caregiverComplianceService.checkClockIn(
            this.userId,
            clientId
          );
        } catch (e) {
          // Non-blocking: if the check fails, don't prevent clock-in
          this.complianceCheck = null;
        }
      }
    },

    async startLocationTracking() {
      // Grab an immediate one-shot fix so the screen populates quickly (this can
      // return a cached fix instantly), then keep it live with a continuous
      // watch that fires whenever the device moves (~1Hz while driving on most
      // devices). This replaces the old 30s polling that left the displayed
      // location stale — and never updated at all if the first fix failed.
      //
      // IMPORTANT: await the one-shot BEFORE starting the watch so we don't fire
      // two concurrent geolocation requests that race the single iOS permission
      // prompt (which produced a spurious "permission denied" before the user
      // had a chance to respond).
      await this.requestLocation();

      if (this.locationWatchId !== null) return;

      this.locationWatchId = geolocationService.watchPosition(
        (position) => {
          this.currentLocation = {
            latitude: position.latitude,
            longitude: position.longitude,
            accuracy: position.accuracy,
          };
          this.locationError = null;
          this.gettingLocation = false;
          // Note: reverse-geocoding the address is done only on the one-shot
          // requestLocation() (initial + manual refresh), not on every watch
          // tick, to avoid hammering the geocoder while the device moves.
        },
        (error) => {
          this.locationError = error.message;
          this.gettingLocation = false;
        },
      );
    },

    clearLocationUpdateTimers() {
      if (this.locationWatchId !== null) {
        geolocationService.clearWatch(this.locationWatchId);
        this.locationWatchId = null;
      }
    },

    calculateDistance() {
      // Distance shown to the user is the distance to the *nearest* known
      // address — that's the one the geofence will accept.
      this.calculatedDistance = this.nearestCandidate?.distance ?? null;
    },

    formatDistance(miles) {
      if (miles === null || miles === undefined) return 'N/A';

      // Convert to feet if less than 0.5 miles
      if (miles < 0.5) {
        const feet = Math.round(miles * 5280);
        return `${feet} feet`;
      }

      return `${miles.toFixed(2)} miles`;
    },

    getDistanceClass() {
      if (!this.calculatedDistance) return '';

      if (this.calculatedDistance <= this.maxAllowedDistance) {
        return 'distance-ok';
      } else if (this.calculatedDistance <= this.maxAllowedDistance * 1.5) {
        return 'distance-warning';
      } else {
        return 'distance-error';
      }
    },

    getAccuracyClass(accuracy) {
      if (accuracy < 50) return 'accuracy-good';
      if (accuracy < 150) return 'accuracy-moderate';
      return 'accuracy-low';
    },

    async reverseGeocode(lat, lng) {
      return `Near ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    },

    checkForAutoSelection() {
      if (this.availableServiceCodes && this.availableServiceCodes.length === 1) {
        this.selectedServiceCodeId = this.availableServiceCodes[0].id;
      }
    },

    handleCancel() {
      this.isVisible = false;
      this.resetForm();
    },

    onSupportStrategyAcknowledged() {
      if (this.supportStrategyCheck) {
        this.supportStrategyCheck.acknowledged = true;
      }
      this.showSupportStrategyModal = false;
    },


    async requestLocation() {
      this.gettingLocation = true;

      try {
        const permissionStatus = await geolocationService.checkPermission();
        // Do NOT hard-block on permissionStatus.state === 'denied' here. The
        // Permissions API is unreliable on iOS/WebKit and can report a stale or
        // false 'denied' before the user has even been prompted. getCurrentPosition
        // below is the authoritative source — if permission is genuinely denied it
        // rejects with PERMISSION_DENIED and the catch surfaces it. (The old gate
        // also called clearLocationUpdateTimers(), which would kill the live watch.)
        if (permissionStatus.state === 'prompt' && this.requiresLocation) {
          this.$notifyInfo('Please allow location access when prompted by your browser to continue.');
        }

        const position = await geolocationService.getCurrentPosition();
        this.currentLocation = {
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
        };
        this.locationError = null;

        this.currentLocationAddress = await this.reverseGeocode(position.latitude, position.longitude);
      } catch (error) {
        let errorMessage = '';
        if (error.message.includes('denied')) {
          errorMessage = 'Location access was denied. Please enable location permissions in your browser settings and try again.';
        } else if (error.message.includes('unavailable')) {
          errorMessage = 'Location services are unavailable. Please check your device settings and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Location request timed out. Please try again or check your internet connection.';
        } else {
          errorMessage = `Location error: ${error.message}. Please try again or contact your administrator.`;
        }
        this.locationError = errorMessage;
        // Only interrupt the caregiver when the geofence is actually enforced;
        // for capture-only codes a failed fix just means no location is stored.
        if (this.requiresLocation) {
          this.$notifyError(errorMessage);
        }
      } finally {
        this.gettingLocation = false;
      }
    },

    onComplianceWarningProceed() {
      // User acknowledged warnings, proceed with clock-in
      this.complianceCheck = { ...this.complianceCheck, acknowledged: true };
      this.confirmClockIn();
    },

    async confirmClockIn() {
      // Support Strategy acknowledgment gate
      if (this.supportStrategyCheck?.required && !this.supportStrategyCheck?.acknowledged) {
        this.showSupportStrategyModal = true;
        return;
      }

      // Compliance document gate
      if (this.complianceCheck && !this.complianceCheck.acknowledged) {
        if (this.complianceCheck.blocks && this.complianceCheck.blocks.length > 0) {
          this.showComplianceWarningModal = true;
          return;
        }
        if (this.complianceCheck.warnings && this.complianceCheck.warnings.length > 0) {
          this.showComplianceWarningModal = true;
          return;
        }
      }

      // Structured probe so "why was I blocked?" is answerable from devtools.
      // Kept gated to keep prod console clean unless a user opts in.
      if (window.DEBUG_GEOFENCE || localStorage.getItem('DEBUG_GEOFENCE') === '1') {
        // eslint-disable-next-line no-console
        console.debug('[geofence] confirmClockIn decision', {
          shiftId: this.shift?.id,
          serviceCode: this.availableServiceCodes.find(sc => sc.id === this.selectedServiceCodeId)?.code,
          requiresLocation: this.requiresLocation,
          geofenceBypassed: this.geofenceBypassed,
          geofenceBypassReason: this.geofenceBypassReason,
          isVirtualShift: this.isVirtualShift,
          currentLocation: this.currentLocation,
          isAccuracyTooLow: this.isAccuracyTooLow,
          maxAllowedDistance: this.maxAllowedDistance,
          isWithinAllowedDistance: this.isWithinAllowedDistance,
          calculatedDistance: this.calculatedDistance,
          candidateDistances: this.candidateDistances.map(c => ({
            source: c.source,
            address: c.address,
            distanceMiles: c.distance,
          })),
          overrideAcknowledged: this.geofenceOverrideAcknowledged,
        });
      }

      // Geofencing gate: require location for geofenced service codes.
      // If no fix has been captured (common while driving — GPS is slow/flaky
      // in motion), don't dead-end: let the caregiver retry or acknowledge the
      // override checkbox so they can still clock in (the attempt is logged).
      if (this.requiresLocation && !this.isVirtualShift && !this.currentLocation) {
        if (!this.geofenceOverrideAcknowledged) {
          this.$notifyError("We couldn't capture your location. Tap \"Refresh location\" to try again, or check the override box to clock in without it (your attempt is logged).");
          return;
        }
      }

      if (this.requiresLocation && !this.isVirtualShift && this.currentLocation && (this.isAccuracyTooLow || !this.isWithinAllowedDistance)) {
        if (!this.geofenceOverrideAcknowledged) {
          this.$notifyError("Please acknowledge the location override checkbox to clock in from this location.");
          return;
        }
      }

      this.clockingIn = true;
      try {
        if (this.availableServiceCodes.length === 0) {
          this.$notifyError("No service codes available for this shift. Cannot clock in.");
          return;
        }

        if (!this.selectedServiceCodeId) {
          this.$notifyError("Please select a service code before clocking in.");
          return;
        }

        const isGroup = this.shift.shift_type === "Group";
        let shiftId = this.shift.id;

        if (this.shift.isPotential) {
          const actualShift = await this.convertPotentialToActual(this.shift);
          shiftId = actualShift.id;
        }

        const now = new Date();
        const payload = {
          shiftId: shiftId,
          caregiverId: this.userId,
          isGroup: isGroup,
          utcTime: now.toISOString(),
          // Idempotency key: if this request is retried (offline queue, or a
          // timeout where the server actually processed it), the API replays
          // the stored outcome instead of double-applying.
          clientEventId: generateEventId(),
        };

        if (this.currentLocation && !this.locationCaptureDisabled) {
          payload.geocode = `${this.currentLocation.latitude},${this.currentLocation.longitude}`;
          payload.locationAccuracy = this.currentLocation.accuracy;
        }

        if (this.geofenceOverrideAcknowledged) {
          payload.geofenceOverride = true;
          payload.geofenceOverrideDistance = this.calculatedDistance;
        }

        if (this.selectedServiceCodeId) {
          payload.serviceCodeId = this.selectedServiceCodeId;
        }

        if (this.isDSPDClient && this.clientLocations.length > 0) {
          // Prefer the nearest matched candidate so the DSPD location we record
          // reflects where the caregiver actually was, not just the first
          // address on file.
          let shiftLocation = null;
          if (this.nearestCandidate) {
            const match = this.clientLocations.find((loc) => {
              const parts = [loc.address1, loc.address2, loc.city, loc.state, loc.zip]
                .filter(Boolean).join(', ');
              return parts === this.nearestCandidate.address;
            });
            shiftLocation = match || this.clientLocations[0];
          } else {
            shiftLocation = this.clientLocations[0];
          }
          if (shiftLocation) {
            payload.selectedLocation = {
              address1: shiftLocation.address1 || '',
              address2: shiftLocation.address2 || '',
              city: shiftLocation.city || '',
              state: shiftLocation.state || '',
              zip: shiftLocation.zip || ''
            };
          }
        }

        // Mileage may be recorded on any shift; the transporting flag only
        // controls whether the daily commute deduction applies.
        payload.isTransporting = this.isTransporting;
        if (this.mileageStartLocation || this.odometerStart) {
          payload.mileageStartLocation = this.mileageStartLocation || null;
          payload.odometerStart = this.odometerStart ? parseFloat(this.odometerStart) : null;
        }

        let response;
        try {
          response = await shiftsService.clockIn(payload);
        } catch (error) {
          // No connection: save the clock-in on this device and upload when
          // the network returns. The caregiver is optimistically shown as
          // clocked in (see utils/pendingClockState.js).
          const isNetworkFailure =
            !navigator.onLine || (error.isAxiosError && !error.response);
          if (!isNetworkFailure) throw error;
          payload.offlineSync = true;
          const clientNames = (this.shift.clients || [])
            .map((c) => c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim())
            .filter(Boolean)
            .join(", ");
          enqueue("clock-in", payload, {
            shiftKey: payload.shiftId,
            label: `Clock-in${clientNames ? ` — ${clientNames}` : ""}`,
          });
          this.$notifySuccess(
            "No internet connection — your clock-in was saved on this device and will upload automatically when you're back online.",
          );
          this.isVisible = false;
          this.resetForm();
          this.$emit("clock-in-success", {
            offlineQueued: true,
            shift: { ...this.shift, id: payload.shiftId },
          });
          return true;
        }
        this.$notifySuccess("Successfully clocked in!");
        // The clock-out most likely to happen in a dead zone belongs to the
        // shift we just clocked into — warm its offline data now.
        if (response?.shift) {
          prefetchShiftClockData(response.shift, this.userId).catch(() => {});
        }
        this.isVisible = false;
        this.resetForm();
        this.$emit('clock-in-success', response);
        return true;
      } catch (error) {
        // Geofencing temporarily disabled
        // if (error.response?.status === 403 && error.response?.data?.distance) {
        //   const errorData = error.response.data;
        //   let message = `❌ Clock-in denied: ${errorData.error}`;
        //   if (errorData.clientName) {
        //     message += `\n\nClient: ${errorData.clientName}`;
        //   }
        //   if (errorData.distance !== undefined && errorData.maxDistance !== undefined) {
        //     message += `\nYour distance: ${Math.round(errorData.distance)} feet`;
        //     message += `\nMaximum allowed: ${errorData.maxDistance} feet`;
        //   }
        //   if (errorData.clientAddress) {
        //     message += `\nClient address: ${errorData.clientAddress}`;
        //   }
        //   this.$notifyError(message);
        // } else {
        this.$notifyError("Error clocking in: " + (error.response?.data?.error || error.response?.data?.message || error.message));
        // }
      } finally {
        this.clockingIn = false;
      }
    },

    async confirmClockInAndJoinMeeting() {
      const success = await this.confirmClockIn();
      if (success && this.shift?.virtual_link) {
        window.open(this.shift.virtual_link, '_blank', 'noopener,noreferrer');
      }
    },

    async convertPotentialToActual(potentialShift) {
      // This method would need to be implemented based on your existing logic
      // For now, return the shift as-is
      return potentialShift;
    },

    openMapLink(location) {
      const address = `${location.address1}${location.address2 ? ', ' + location.address2 : ''}, ${location.city}, ${location.state} ${location.zip}`;
      const encodedAddress = encodeURIComponent(address);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, '_blank');
    },

    formatTableDate(date) {
      if (!date) return '';

      // Handle date string to avoid timezone issues
      let dateStr = date;
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0]; // Extract just the date part
      }

      // Create date object with explicit local timezone to avoid UTC interpretation
      const [year, month, day] = dateStr.split('-');
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed

      return dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    formatTimeRange(startTime, endTime, date = null) {
      // Handle new UTC datetime format (with 'T' in the string)
      if (startTime && startTime.includes('T')) {
        const start = this.formatScheduledTime(startTime);
        const end = this.formatScheduledTime(endTime);
        return `${start} - ${end}`;
      }

      // Legacy: handle old format with separate date and time
      if (date && startTime && endTime) {
        const start = this.formatScheduledTime(startTime, date);
        const end = this.formatScheduledTime(endTime, date);
        return `${start} - ${end}`;
      }

      if (!startTime || !endTime) return '';

      // Fallback: Convert UTC times to local times first and format
      const localStartTime = this.convertUTCTimeToLocalTime(startTime);
      const localEndTime = this.convertUTCTimeToLocalTime(endTime);

      const formatTime = (timeString) => {
        const timeParts = timeString.split(':');
        let hours = parseInt(timeParts[0]);
        const minutes = timeParts[1] || '00';
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const formattedMinutes = minutes === '00' ? '00' : minutes;
        return `${hours}:${formattedMinutes}${ampm}`;
      };

      return `${formatTime(localStartTime)} - ${formatTime(localEndTime)}`;
    },

    convertUTCTimeToLocalTime(timeStr) {
      if (!timeStr || !this.shift?.date) return timeStr;

      try {
        const shiftDate = this.shift.date.split('T')[0];
        const utcDateTime = `${shiftDate}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (isNaN(utcDate.getTime())) {
          return timeStr;
        }

        return utcDate.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return timeStr;
      }
    },

    formatClient(shift) {
      if (!shift) return '';
      const clients = shift.clients || shift.originalData?.clients || [];
      if (clients.length > 0) {
        // For group shifts, show all clients
        if (shift.shift_type === 'Group') {
          return clients.map(client => {
            const name = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim();
            return name || "Unnamed Client";
          }).join(', ');
        }
        // For non-group shifts, show only the first client
        const client = clients[0];
        const name = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim();
        return name || "Unnamed Client";
      }
      return shift.client || "Unassigned";
    },

    formatCaregiver(shift) {
      if (!shift) return '';
      if (shift.caregivers && shift.caregivers.length > 0) {
        // Show all caregivers
        return shift.caregivers.map(caregiver => {
          const name = caregiver.name || `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.trim();
          return name || "Unnamed Caregiver";
        }).join(', ');
      }
      return shift.caregiver || "Unassigned";
    },

    formatScheduledTime(timeStr, date = null) {
      if (!timeStr) return "N/A";

      // If timeStr is already a full datetime (ISO string), use it directly
      if (timeStr.includes('T')) {
        const utcDate = new Date(timeStr);
        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
        // If the date parsing failed, try to extract time portion
        const timeMatch = timeStr.match(/T(\d{2}):(\d{2})/);
        if (timeMatch) {
          const hour = parseInt(timeMatch[1]);
          const minute = timeMatch[2];
          const period = hour >= 12 ? "PM" : "AM";
          const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          return `${displayHour}:${minute} ${period}`;
        }
      }

      // Legacy: If we have a date, combine it with the time and treat as UTC
      if (date) {
        const dateOnly = date.split('T')[0];
        const utcDateTime = `${dateOnly}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Fallback: treat as local time
      return this.formatTime12Hour(timeStr);
    },

    formatTime12Hour(timeStr) {
      if (!timeStr) return "";

      // Handle both HH:MM and HH:MM:SS formats
      const timeParts = timeStr.split(":");
      if (timeParts.length < 2) return timeStr; // Return as-is if not in expected format

      const hour = parseInt(timeParts[0]);
      const minute = parseInt(timeParts[1]);

      // Check for invalid numbers
      if (isNaN(hour) || isNaN(minute)) return timeStr;

      const period = hour >= 12 ? "pm" : "am";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
    }
  }
};
</script>

<style scoped>
.mileage-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 12px;
}

.mileage-section :deep(.el-checkbox__label) {
  font-size: 1.05rem;
}

.mileage-fields {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
}

.clock-modal-content {
  max-width: 600px;
  min-width: 500px;
  margin: 0 auto;
  padding: 0 2rem;
}

.clock-modal-content .mb-3 {
  margin-bottom: 1.5rem;
}

.clock-modal-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6BA6CD;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.clock-modal-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.clock-modal-content strong {
  color: #fff;
  font-weight: 600;
}

.shift-notes-section {
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.shift-notes-section strong {
  color: #fff;
  font-weight: 600;
}

.shift-notes-section i {
  color: #6BA6CD;
  margin-right: 0.35rem;
}

.shift-notes-content {
  margin-top: 0.4rem;
  background: rgba(107, 166, 205, 0.1);
  border: 1px solid rgba(107, 166, 205, 0.25);
  border-radius: 6px;
  padding: 0.6rem 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
  color: #e0e0e0;
  font-size: 0.9rem;
  line-height: 1.45;
}

.form-label {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.95rem;
}

.service-code-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.service-code-label {
  margin-bottom: 0;
  white-space: nowrap;
}

.text-success {
  color: #28a745 !important;
  font-weight: 500;
}

.location-selector {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.location-option {
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
}

.location-details {
  margin-left: 0.5rem;
}

.location-address {
  color: #fff;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.map-link-btn {
  color: #6BA6CD !important;
  padding: 0 !important;
  font-size: 0.85rem;
}

.map-link-btn:hover {
  color: #8BC4E0 !important;
}

.text-muted {
  color: #b0b0b0 !important;
}

.no-service-code-message .alert {
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  color: #f8d7da;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.no-service-code-message .alert i {
  color: #dc3545;
}

.location-info-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.location-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.location-row:last-child {
  margin-bottom: 0;
}

.location-label {
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  min-width: 140px;
}

.location-label i {
  color: #6BA6CD;
  margin-right: 0.5rem;
  width: 16px;
  text-align: center;
}

.location-value {
  color: #e0e0e0;
  font-size: 0.9rem;
  text-align: right;
  word-break: break-all;
}

.location-pending {
  color: #b0b0b0;
  font-size: 0.9rem;
  padding: 0.5rem 0;
}

.refresh-btn {
  color: #6BA6CD !important;
  font-size: 0.85rem;
}

.refresh-btn:hover {
  color: #8BC4E0 !important;
}

.distance-info {
  background: rgba(107, 166, 205, 0.1);
  border: 1px solid rgba(107, 166, 205, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.distance-display {
  color: #fff;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.distance-display i {
  color: #6BA6CD;
}

.distance-limit {
  color: #b0b0b0;
  font-size: 0.85rem;
  margin-left: 0.5rem;
}

.distance-ok {
  color: #28a745;
}

.distance-warning {
  color: #ffc107;
}

.distance-error {
  color: #dc3545;
}

.distance-warning {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  color: #f8d7da;
  padding: 0.75rem;
  border-radius: 6px;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.distance-warning i {
  color: #dc3545;
}

.accuracy-good {
  color: #28a745 !important;
}

.accuracy-moderate {
  color: #ffc107 !important;
}

.accuracy-low {
  color: #dc3545 !important;
}

.accuracy-warning-banner {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  color: #f8d7da;
  padding: 0.75rem;
  border-radius: 6px;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.accuracy-warning-banner i {
  color: #dc3545;
  flex-shrink: 0;
}

.location-refresh-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
}

.location-error {
  color: #f8d7da;
  font-size: 0.8rem;
}

.geofence-override-section {
  margin-top: 0.75rem;
}

.geofence-override-banner {
  background: rgba(255, 152, 0, 0.12);
  border: 1px solid rgba(255, 152, 0, 0.4);
  color: #ffe0b2;
  padding: 0.75rem;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.geofence-override-banner > i {
  color: #FF9800;
  margin-top: 2px;
  flex-shrink: 0;
}

.geofence-override-banner .override-hint {
  margin: 0.4rem 0 0;
  font-size: 0.82rem;
  color: #ccc;
}

.geofence-override-checkbox {
  margin-top: 0.5rem;
  color: #FF9800 !important;
}

.geofence-bypass-banner {
  background: rgba(33, 150, 243, 0.12);
  border: 1px solid rgba(33, 150, 243, 0.4);
  color: #cfe9fc;
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.geofence-bypass-banner i {
  color: #2196F3;
  flex-shrink: 0;
}

.extra-address-hint {
  display: block;
  font-size: 0.78rem;
  color: #aaa;
  margin-top: 2px;
}

.geofence-override-checkbox .el-checkbox__label {
  color: #FF9800 !important;
  font-size: 0.85rem;
}

.medication-client-name {
  color: #6BA6CD;
  font-weight: 600;
  font-size: 0.9rem;
  margin: 0.75rem 0 0.4rem;
}

.medication-info-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}

.medication-name {
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
}

.medication-details {
  color: #b0b0b0;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.medication-instructions {
  color: #999;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-style: italic;
}

.virtual-link-value {
  color: #6BA6CD;
  font-size: 0.9rem;
  text-align: right;
  word-break: break-all;
  text-decoration: underline;
}

.virtual-link-value:hover {
  color: #8BC4E0;
}

/* Clock In button - matches "Clocked In" status color (bright yellow) */
.clock-in-btn {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 193, 7, 0.7)) !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
  color: #000 !important;
}
.clock-in-btn:hover {
  background: linear-gradient(135deg, rgba(255, 193, 7, 1), rgba(255, 193, 7, 0.8)) !important;
  border: 1px solid rgba(0, 0, 0, 0.3) !important;
  color: #000 !important;
}

@media (max-width: 768px) {
  /* Make modal wider on mobile devices */
  :deep(.el-dialog) {
    width: 95% !important;
    margin: 5px auto !important;
  }

  .clock-modal-content {
    min-width: auto !important;
    padding: 0 1rem;
  }

  .location-option {
    padding: 0.75rem;
  }

  .location-info-card {
    padding: 0.75rem;
  }

  /* Stack location information vertically on mobile */
  .location-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .location-label {
    min-width: auto;
    margin-bottom: 0.25rem;
  }

  .location-value {
    text-align: left;
    word-break: normal;
    overflow-wrap: break-word;
    width: 100%;
  }
}
</style>
